/**
 * The country's window as its own lane of the hour chart: a band over the
 * window's hours ("09:00–21:00 · 🇩🇰 00:00–12:00"), so the best hours read
 * straight against it (user, 12/10/2026). Two bands past midnight.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { dkLabel } from '@/features/drop-generator/utils/clock/dk-label';
import { minuteLabel, type CountryWindow } from '@/features/drop-generator/components/editor/general/hour/hour-chart-math';

interface Props {
  window: CountryWindow;
  timezone: string;
}

const pct = (minute: number) => (minute / 1440) * 100;

export const WindowLane: FC<Props> = ({ window: w, timezone }) => {
  const parts = w.startMin <= w.endMin ? [[w.startMin, w.endMin]] : [[w.startMin, 1440], [0, w.endMin]];
  const widest = parts.reduce((best, part) => (part[1] - part[0] > best[1] - best[0] ? part : best), parts[0]);
  const dkFrom = dkLabel(w.startMin, timezone)?.slice(0, 5);
  const dkTo = dkLabel(w.endMin, timezone)?.slice(0, 5);
  return (
    <div className="relative h-5">
      {parts.map(([from, to]) => (
        <span
          key={from}
          className="absolute inset-y-0 flex items-center justify-center overflow-hidden rounded-md bg-primary/10 ring-1 ring-inset ring-primary/30 transition-[left,width] duration-500 ease-out motion-reduce:transition-none"
          style={{ left: `${pct(from)}%`, width: `${pct(to - from)}%` }}
        >
          {from === widest[0] ? (
            <span className="truncate px-1.5 font-mono text-[10px] font-semibold tabular-nums text-primary">
              {dkFrom && dkTo ? <>🇩🇰 {dkFrom}–{dkTo} · </> : null}
              <span className={dkFrom && dkTo ? 'font-normal text-primary/70' : undefined}>
                {minuteLabel(w.startMin)}–{minuteLabel(w.endMin)}
              </span>
            </span>
          ) : null}
        </span>
      ))}
    </div>
  );
};
